import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ImageUpload from "src/Components/GlobalComponents/ImageUplaod";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import httpRequest from "src/helpers/httpRequest";
import {
  IHomePageInterface,
  IProduct,
  IProductItems,
  ITagsItems,
} from "src/types/GlobalInterfaces";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { canWrite } from "src/helpers/helperFunctions";
import SelectDropdown from "src/Components/GlobalComponents/SelectDropdown";
import { IPaginated } from "src/types/paginated";
import SimpleInput from "src/Components/SimpleInput";
import CustomWebsiteDropdown from "src/Components/GlobalComponents/Widget/CustomWebsiteDropdown";

interface ProductCategoryType {
  mainCategoryID: string;
  subCategoryID: string;
  childCategoryID: string;
  productID: string;
}

interface ICategoryResponse {
  documents: any[];
  paginated: IPaginated;
}

const ProductcategorySection: React.FC<{
  allCategories: any[];
  data: {
    heading: string;
    products: (IProduct | null)[];
  } | null;
  setData: React.Dispatch<React.SetStateAction<IHomePageInterface | null>>;
}> = ({ allCategories, data, setData }) => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "platformManagement", false);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [heading, setHeading] = useState<string>("");
  const [allMainCategories, setAllMainCategories] = useState<any[]>([]);
  const [allSubCategories, setAllSubCategories] = useState<any[]>([]);
  const [allChildCategories, setAllChildCategories] = useState<any[]>([]);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [productCategory, setProductCategory] = useState<ProductCategoryType[]>(
    Array(8)
      .fill(null)
      .map(() => ({
        mainCategoryID: "",
        subCategoryID: "",
        childCategoryID: "",
        productID: "",
      }))
  );
  const [categoriesLoader, setCategoriesLoader] = useState<boolean>(false);
  const [categories, setCategories] = useState<ITagsItems[]>([]);

  const getAllCategories = async () => {
    setCategoriesLoader(true);
    const { res, err } = await httpRequest<ICategoryResponse>({
      path: `/tags/promotional/all`,
    });
    if (res) {
      setCategories(res?.documents);
    } else {
      toast(err, { type: "error" });
    }
    setCategoriesLoader(false);
  };

  const getProductCategory = async () => {
    const { res, err } = await httpRequest<any>({
      path: `/website-manage/all/categories-products`,
    });
    if (res) {
      console.log("Product Category Response ", res);
      setAllMainCategories(res?.mainCategories);
      setAllSubCategories(res?.subCategories);
      setAllChildCategories(res?.childCategories);
      setAllProducts(res?.pharmaProducts);
    } else {
      toast(err, { type: "error" });
    }
  };

  useEffect(() => {
    getAllCategories();
    getProductCategory();
  }, []);

  const handleSubmit = async () => {
    if (!hasWriteAccess) {
      return;
    }

    if (!heading) {
      toast("Heading is required", { type: "error" });
      return;
    }

    if (productCategory.some((tm) => !tm?.productID)) {
      toast("Please select all products", { type: "error" });
      return;
    }

    setIsLoader(true);
    let { res, err } = await httpRequest({
      path: "/website-manage/section-four",
      method: "put",
      params: {
        heading: heading,
        products: productCategory.map((tm) => tm?.productID || null),
      },
    });

    if (res) {
      toast("Product category updated successfully", { type: "success" });
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };

  useEffect(() => {
    if (data?.products) {
      setProductCategory(
        data.products
          .map((item, index) => ({
            mainCategoryID: item?.mainCategoryId || "",
            subCategoryID: item?.subCategoryId || "",
            childCategoryID: item?.childCategoryId || "",
            productID: item?._id || "",
          }))
          .concat(
            Array(8 - (data.products.length || 0))
              .fill(null)
              .map(() => ({
                mainCategoryID: "",
                subCategoryID: "",
                childCategoryID: "",
                productID: "",
              }))
          )
      );
      setHeading(data?.heading || "");
    } else {
      setProductCategory(
        Array(8)
          .fill(null)
          .map(() => ({
            mainCategoryID: "",
            subCategoryID: "",
            childCategoryID: "",
            productID: "",
          }))
      );
      setHeading("");
    }
  }, [data]);

  const CheckDuplicates = (tm: IProduct, index: number) => {
    let allData = productCategory.filter((t, i) => i !== index);
    return allData.find((t) => t.productID === tm._id) ? true : false;
  };

  return (
    <div className="section__1">
      <h1 className="font-Poppins-SemiBold font-size-25px mb-2 theme-color-green">
        Section 04
      </h1>
      <div className="products-tabs-content">
        <div>
          <h1 className="font-size-18px text-[#2F2F31] mb-3 font-Poppins-Regular">
            Heading
          </h1>
          <SimpleInput
            labelText="Heading"
            placeholder="Type Heading"
            value={heading}
            onChange={(value: string) => setHeading(value || "")}
            id="product-category-heading"
            disabled={!hasWriteAccess}
          />
          <div className="w-full grid grid-cols-4 gap-4 gap-x-10">
            {productCategory.map((item, ind) => {
              return (
                <div className="w-full" key={ind}>
                  <h1 className="font-size-18px text-[#2F2F31] mb-3 font-Poppins-Regular">
                    Product {String(ind + 1).padStart(2, "0")}
                  </h1>
                  {/* Main Category */}
                  <CustomWebsiteDropdown
                    loading={categoriesLoader}
                    options={allMainCategories.map((tm) => ({
                      label: tm.categoryName,
                      value: tm._id,
                    }))}
                    data={
                      item?.mainCategoryID && allMainCategories
                        ? [
                            {
                              label:
                                allMainCategories.find(
                                  (tm) => tm._id === item?.mainCategoryID
                                )?.categoryName || "",
                              value: item?.mainCategoryID,
                            },
                          ]
                        : []
                    }
                    setData={(val) => {
                      console.log("Selected Tag", val);
                      setProductCategory((prev) => {
                        const newBanner = [...prev];
                        newBanner[ind] = {
                          ...newBanner[ind],
                          mainCategoryID: val[0]?.value || "",
                          subCategoryID: "", // Reset subCategoryID when mainCategoryID changes
                          childCategoryID: "", // Reset childCategoryID when mainCategoryID changes
                          productID: "", // Reset productID when mainCategoryID changes
                        };
                        return newBanner;
                      });
                    }}
                    placeholder="Select Main Category"
                    dropdownMainId={`product-category-main-${ind + 1}`}
                    disabled={!hasWriteAccess}
                  />
                  {/* Sub Category */}

                  <CustomWebsiteDropdown
                    loading={categoriesLoader}
                    options={allSubCategories
                      .filter(
                        (tm) => tm?.mainCategoryId === item?.mainCategoryID
                      )
                      .map((tm) => ({
                        label: tm.subCategoryName,
                        value: tm._id,
                      }))}
                    data={
                      item?.subCategoryID && allSubCategories
                        ? [
                            {
                              label:
                                allSubCategories.find(
                                  (tm) => tm._id === item?.subCategoryID
                                )?.subCategoryName || "",
                              value: item?.subCategoryID,
                            },
                          ]
                        : []
                    }
                    setData={(val) => {
                      console.log("Selected Tag", val);
                      setProductCategory((prev) => {
                        const newBanner = [...prev];
                        newBanner[ind] = {
                          ...newBanner[ind],
                          subCategoryID: val[0]?.value || "",
                          childCategoryID: "", // Reset childCategoryID when subCategoryID changes
                          productID: "", // Reset productID when subCategoryID changes
                        };
                        return newBanner;
                      });
                    }}
                    placeholder="Select Sub Category"
                    dropdownMainId={`product-category-sub-${ind + 1}`}
                    disabled={!hasWriteAccess}
                  />

                  {/* Child Category */}

                  <CustomWebsiteDropdown
                    loading={categoriesLoader}
                    options={allChildCategories
                      .filter((tm) => tm?.subCategoryId === item?.subCategoryID)
                      .map((tm) => ({
                        label: tm.childCategoryName,
                        value: tm._id,
                      }))}
                    data={
                      item?.childCategoryID && allChildCategories
                        ? [
                            {
                              label:
                                allChildCategories.find(
                                  (tm) => tm._id === item?.childCategoryID
                                )?.childCategoryName || "",
                              value: item?.childCategoryID,
                            },
                          ]
                        : []
                    }
                    setData={(val) => {
                      console.log("Selected Tag", val);
                      setProductCategory((prev) => {
                        const newBanner = [...prev];
                        newBanner[ind] = {
                          ...newBanner[ind],
                          childCategoryID: val[0]?.value || "",
                          productID: "", // Reset productID when childCategoryID changes
                        };
                        return newBanner;
                      });
                    }}
                    placeholder="Select Child Category"
                    dropdownMainId={`product-category-child-${ind + 1}`}
                    disabled={!hasWriteAccess}
                  />

                  {/* Product */}

                  <CustomWebsiteDropdown
                    loading={categoriesLoader}
                    options={allProducts
                      .filter(
                        (tm) =>
                          tm?.mainCategoryId === item?.mainCategoryID &&
                          tm?.subCategoryId === item?.subCategoryID &&
                          tm?.childCategoryId === item?.childCategoryID
                      )
                      .map((tm) => ({
                        label: tm.productName,
                        value: tm._id,
                        disabled: CheckDuplicates(tm, ind),
                      }))}
                    data={
                      item?.productID && allProducts
                        ? [
                            {
                              label:
                                allProducts.find(
                                  (tm) => tm._id === item?.productID
                                )?.productName || "",
                              value: item?.productID,
                            },
                          ]
                        : []
                    }
                    setData={(val) => {
                      console.log("Selected Tag", val);
                      setProductCategory((prev) => {
                        const newBanner = [...prev];
                        newBanner[ind] = {
                          ...newBanner[ind],
                          productID: val[0]?.value || "",
                        };
                        return newBanner;
                      });
                    }}
                    placeholder="Select Product"
                    dropdownMainId={`product-category-product-${ind + 1}`}
                    disabled={!hasWriteAccess}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex mt-2 justify-end">
        <PrimaryBtn
          disabled={
            typeof productCategory === "object"
              ? false
              : true || !hasWriteAccess
          }
          loader={isLoader}
          ButtonText="Save"
          btnExtraStyle={{
            backgroundColor: "#00443f",
            borderRadius: "1.5rem",
          }}
          onPress={() => {
            if (!hasWriteAccess) {
              return;
            }
            handleSubmit();
          }}
          btnExtralClasses={`${!hasWriteAccess ? "cursor-not-allowed" : ""}`}
          id="product-category-save"
        />
      </div>
    </div>
  );
};

export default ProductcategorySection;
